import React, { createContext, useContext, useReducer, useCallback } from 'react';

const ThreadsContext = createContext();

// Action types
const ACTIONS = {
  SET_THREADS: 'SET_THREADS',
  ADD_THREAD: 'ADD_THREAD',
  UPDATE_THREAD: 'UPDATE_THREAD',
  DELETE_THREAD: 'DELETE_THREAD',
  ADD_COMMENT: 'ADD_COMMENT',
  UPDATE_COMMENT: 'UPDATE_COMMENT',
  DELETE_COMMENT: 'DELETE_COMMENT',
  TOGGLE_LIKE: 'TOGGLE_LIKE',
  TOGGLE_COMMENT_LIKE: 'TOGGLE_COMMENT_LIKE',
  MARK_AS_READ: 'MARK_AS_READ',
  TOGGLE_ANCHOR: 'TOGGLE_ANCHOR',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
};

// Initial state
const initialState = {
  threads: [],
  loading: false,
  error: null,
};

// Reducer
function threadsReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_THREADS:
      return {
        ...state,
        threads: action.payload,
        loading: false,
        error: null,
      };

    case ACTIONS.ADD_THREAD:
      return {
        ...state,
        threads: [action.payload, ...state.threads],
      };

    case ACTIONS.UPDATE_THREAD:
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload.id
            ? { ...thread, ...action.payload }
            : thread
        ),
      };

    case ACTIONS.DELETE_THREAD:
      return {
        ...state,
        threads: state.threads.filter(thread => thread.id !== action.payload),
      };

    case ACTIONS.ADD_COMMENT:
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload.threadId
            ? {
                ...thread,
                comments: [...(thread.comments || []), action.payload.comment],
              }
            : thread
        ),
      };

    case ACTIONS.UPDATE_COMMENT:
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload.threadId
            ? {
                ...thread,
                comments: thread.comments.map(comment =>
                  comment.id === action.payload.commentId
                    ? { ...comment, ...action.payload.data }
                    : comment
                ),
              }
            : thread
        ),
      };

    case ACTIONS.DELETE_COMMENT:
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload.threadId
            ? {
                ...thread,
                comments: thread.comments.filter(
                  comment => comment.id !== action.payload.commentId
                ),
              }
            : thread
        ),
      };

    case ACTIONS.TOGGLE_LIKE:
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload.threadId
            ? {
                ...thread,
                likes: action.payload.isLiked
                  ? thread.likes.filter(like => like.user.id !== action.payload.userId)
                  : [...thread.likes, { user: { id: action.payload.userId } }],
              }
            : thread
        ),
      };

    case ACTIONS.TOGGLE_COMMENT_LIKE:
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload.threadId
            ? {
                ...thread,
                comments: thread.comments.map(comment =>
                  comment.id === action.payload.commentId
                    ? {
                        ...comment,
                        likes: action.payload.isLiked
                          ? comment.likes.filter(like => like.user.id !== action.payload.userId)
                          : [...comment.likes, { user: { id: action.payload.userId } }],
                      }
                    : comment
                ),
              }
            : thread
        ),
      };

    case ACTIONS.MARK_AS_READ:
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload.threadId
            ? {
                ...thread,
                seen_by: thread.seen_by.includes(action.payload.userId)
                  ? thread.seen_by
                  : [...thread.seen_by, action.payload.userId],
              }
            : thread
        ),
      };

    case ACTIONS.TOGGLE_ANCHOR:
      return {
        ...state,
        threads: state.threads.map(thread =>
          thread.id === action.payload
            ? { ...thread, anchored: !thread.anchored }
            : thread
        ),
      };

    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    default:
      return state;
  }
}

// Provider component
export function ThreadsProvider({ children }) {
  const [state, dispatch] = useReducer(threadsReducer, initialState);

  // Actions
  const setThreads = useCallback((threads) => {
    dispatch({ type: ACTIONS.SET_THREADS, payload: threads });
  }, []);

  const addThread = useCallback((thread) => {
    dispatch({ type: ACTIONS.ADD_THREAD, payload: thread });
  }, []);

  const updateThread = useCallback((threadId, data) => {
    dispatch({ type: ACTIONS.UPDATE_THREAD, payload: { id: threadId, ...data } });
  }, []);

  const deleteThread = useCallback((threadId) => {
    dispatch({ type: ACTIONS.DELETE_THREAD, payload: threadId });
  }, []);

  const addComment = useCallback((threadId, comment) => {
    dispatch({ type: ACTIONS.ADD_COMMENT, payload: { threadId, comment } });
  }, []);

  const updateComment = useCallback((threadId, commentId, data) => {
    dispatch({ 
      type: ACTIONS.UPDATE_COMMENT, 
      payload: { threadId, commentId, data } 
    });
  }, []);

  const deleteComment = useCallback((threadId, commentId) => {
    dispatch({ 
      type: ACTIONS.DELETE_COMMENT, 
      payload: { threadId, commentId } 
    });
  }, []);

  const toggleLike = useCallback((threadId, userId, isLiked) => {
    dispatch({ 
      type: ACTIONS.TOGGLE_LIKE, 
      payload: { threadId, userId, isLiked } 
    });
  }, []);

  const toggleCommentLike = useCallback((threadId, commentId, userId, isLiked) => {
    dispatch({ 
      type: ACTIONS.TOGGLE_COMMENT_LIKE, 
      payload: { threadId, commentId, userId, isLiked } 
    });
  }, []);

  const markAsRead = useCallback((threadId, userId) => {
    dispatch({ 
      type: ACTIONS.MARK_AS_READ, 
      payload: { threadId, userId } 
    });
  }, []);

  const toggleAnchor = useCallback((threadId) => {
    dispatch({ type: ACTIONS.TOGGLE_ANCHOR, payload: threadId });
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: error });
  }, []);

  const value = {
    // State
    threads: state.threads,
    loading: state.loading,
    error: state.error,
    
    // Actions
    setThreads,
    addThread,
    updateThread,
    deleteThread,
    addComment,
    updateComment,
    deleteComment,
    toggleLike,
    toggleCommentLike,
    markAsRead,
    toggleAnchor,
    setLoading,
    setError,
  };

  return (
    <ThreadsContext.Provider value={value}>
      {children}
    </ThreadsContext.Provider>
  );
}

// Custom hook to use the context
export function useThreads() {
  const context = useContext(ThreadsContext);
  if (!context) {
    throw new Error('useThreads must be used within a ThreadsProvider');
  }
  return context;
}

export { ACTIONS };